import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useToast } from "../../components/ToastContext";
import { api } from '../../services/api';

const CreateUser = ({ open, onClose, userData, onSuccess }) => {
  const theme = useTheme();
  const toast = useToast();

  const [roles, setRoles] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const userId = sessionStorage.getItem('userId');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    roleId: '',
    organizationId: '',
    gender: '',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    setErrors({});
    
    if (open && userData) {
      setFormData({
        name: userData.name || '',
        email: userData.userName || userData.email || '',
        password: '',
        roleId: userData.roleId || '',
        organizationId: userData.organizationId || '',
        gender: userData.gender ? userData.gender.toLowerCase() : '',
      });
    } else if (open && !userData) {
      setFormData({
        name: '',
        email: '',
        password: '',
        roleId: '',
        organizationId: '',
        gender: '',
      });
    }
  }, [open, userData]);

  useEffect(() => {
    if (open) {
      const fetchData = async () => {
        try {
          const correlationId = 'b6bf5a0f-fadf-4718-9970-91d1af4756fe';
          const [orgRes, rolesRes] = await Promise.all([
            api.get(`/Client/GetOrganizationDetailsByUserId/${userId}`, { headers: { 'X-CORRELATION-ID': correlationId } }),
            api.get('/Account/userRoles', { headers: { 'X-CORRELATION-ID': correlationId } })
          ]);
          setOrganizations(orgRes.data || []);
          setRoles(rolesRes.data || []);
        } catch (error) {
          console.error('Error fetching dropdowns:', error);
        }
      };
      fetchData();
    }
  }, [open, userId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === "organizationId" ? Number(value) : value
    });
    
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  const handleSubmit = async () => {
    let newErrors = {};
    if (!formData.name) newErrors.name = "Name is required";
    if (!formData.email) newErrors.email = "Email is required";
    if (!userData && !formData.password) newErrors.password = "Password is required";
    // if (!formData.roleId) newErrors.roleId = "Role is required";
    if (!formData.gender) newErrors.gender = "Gender is required";
    if (!formData.organizationId) newErrors.organizationId = "Organization is required"; 

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fill all the required fields.");
      return;
    }

    try {
      let response;
      const headers = {
        'X-CORRELATION-ID': '2263359b-9b5b-44d4-b0e4-a84552ee093d',
        'Content-Type': 'application/json',
      };

      if (userData?.userId) {
        const updatePayload = {
          ...userData,
          name: formData.name,
          gender: formData.gender,
          organizationId: formData.organizationId,
          roleId: formData.roleId || " ",
          requester: userId
        };
        response = await api.post(`/Account/UpdateUserDetails/${userData.userId}`, updatePayload, { headers });
      } else {
        const createPayload = {
          ...formData,
          organizationId: formData.organizationId,
          requester: userId
        };
        response = await api.post('/Account/CreateUser', createPayload, { headers });
      }

      if (response.status === 200 || response.status === 201) {
        toast.success(`User ${userData ? 'updated' : 'created'} successfully.`);
        onSuccess();
        handleClose();
      } else {
        toast.error(`Failed to ${userData ? 'update' : 'create'} user.`);
      }
    } catch (error) {
      const apiErrorMessage = error.response?.data?.errorMessages?.[0]?.message;
      
      if (apiErrorMessage) {
        toast.error(apiErrorMessage);
      } else {
        toast.error(error.message || 'Error saving user.');
      }
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: theme.palette.background.default, px: 2, mb: 2, fontSize: '20px', fontWeight: 700 }}>
        {userData ? 'Edit User' : 'Create User'}
      </DialogTitle>

      <DialogContent
        sx={{
          bgcolor: theme.palette.background.paper,
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
          pt: 2
        }}
      >
        <Box sx={{ display: 'flex', gap: 2.5, pt: 2 }}>
          <TextField
            label="Name *"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            fullWidth
            error={!!errors.name}
            helperText={errors.name}
          />
          <TextField
            select
            label="Gender *"
            name="gender"
            value={formData.gender}
            onChange={handleInputChange}
            fullWidth
            error={!!errors.gender}
            helperText={errors.gender}
          >
            <MenuItem value="male">Male</MenuItem>
            <MenuItem value="female">Female</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>
        </Box>

        <Box sx={{ display: 'flex', gap: 2.5 }}>
          <TextField
            label="Email *"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            fullWidth
            disabled={!!userData}
            autoComplete="nope"
            error={!!errors.email}
            helperText={errors.email}
            sx={{
              '& input:-webkit-autofill': {
                WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.paper} inset !important`,
                WebkitTextFillColor: `${theme.palette.text.primary} !important`,
                transition: 'background-color 5000s ease-in-out 0s',
              }
            }}
          />
          {!userData && (
            <TextField
              label="Password *"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              fullWidth
              autoComplete="new-password"
              error={!!errors.password}
              helperText={errors.password}
              sx={{
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.paper} inset !important`,
                  WebkitTextFillColor: `${theme.palette.text.primary} !important`,
                  transition: 'background-color 5000s ease-in-out 0s',
                }
              }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2.5 }}>
          {/* <TextField
            select
            label="Role *"
            name="roleId"
            value={formData.roleId}
            onChange={handleInputChange}
            fullWidth
            error={!!errors.roleId}
            helperText={errors.roleId}
          >
            {roles.map((role) => (
              <MenuItem key={role.value} value={role.value}>
                {role.text}
              </MenuItem>
            ))}
          </TextField> */}

          <TextField
            select
            label="Organization *"
            name="organizationId"
            value={formData.organizationId}
            onChange={handleInputChange}
            fullWidth
            error={!!errors.organizationId}
            helperText={errors.organizationId}
          >
            {organizations
              .filter((org) => org.isActive === true)
              .map((org) => (
                <MenuItem key={org.organizationId} value={org.organizationId}>
                  {org.organizationName}
                </MenuItem>
              ))}
          </TextField>
        </Box>
      </DialogContent>

      <DialogActions sx={{ py: 1.5, px: 3, bgcolor: theme.palette.background.default, justifyContent: 'flex-end' }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            p: 0.7,
            px: 2,
            color: theme.palette.mode === 'dark' ? 'grey.300' : 'text.secondary',
            borderColor: theme.palette.mode === 'dark' ? 'grey.700' : 'grey.400',
            '&:hover': {
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
              borderColor: theme.palette.mode === 'dark' ? 'grey.500' : 'grey.600',
            }
          }}
        >
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" sx={{ py: 1.2 }}>
          {userData ? 'Save Changes' : 'Create User'}
        </Button>
      </DialogActions>

    </Dialog>
  );
};

export default CreateUser;